import { useMemo } from "react";
import type { DefaultValuesOption } from "@stripe/stripe-js";
import { useFormContext } from "react-hook-form";
import { usePaywallCheckoutContext } from "@/react/contexts/Paywalls/paywallCheckoutContext";
import { getCurrentUserName } from "../../../helpers/users";
import type { CheckoutFormValuesProps } from "../../../types/CheckoutFormValuesProps";

export const usePaymentElementDefaultValues = (): DefaultValuesOption => {
  const { watch } = useFormContext<CheckoutFormValuesProps>();
  const { currentUser, memberBillingInfo } = usePaywallCheckoutContext();

  const emailFieldValue = watch("email");
  const nameFieldValue = watch("name");

  return useMemo(() => {
    const billingDetails: DefaultValuesOption["billingDetails"] = {};

    // If there is a community member logged in, we prefill the Payment Element with their email
    // and name.
    //
    // Otherwise, we use the values from the email and name fields in our form to force the
    // Payment Element to re-render and avoid showing the email and name fields as we have dedicated
    // fields for them in our form.
    const email = currentUser?.email ?? emailFieldValue;
    billingDetails.email = email;

    const name = getCurrentUserName(currentUser) ?? nameFieldValue;
    billingDetails.name = name;

    // If there is a member logged in and that has already provided an address or we have pre-filled
    // the address country, we prefill the Payment Element with it.
    //
    // Otherwise, we rely on the Address Element to provide the Payment Element with the address
    // values it requires to confirm the payment.
    if (memberBillingInfo?.address_country) {
      billingDetails.address = {
        line1: memberBillingInfo.address_line1,
        line2: memberBillingInfo.address_line2 || undefined,
        city: memberBillingInfo.address_city,
        state: memberBillingInfo.address_state,
        postal_code: memberBillingInfo.address_postal_code,
        country: memberBillingInfo.address_country,
      };
    }

    return { billingDetails };
  }, [currentUser, memberBillingInfo, emailFieldValue, nameFieldValue]);
};
